
import { Link, useLocation } from "react-router-dom";
import { LucideIcon, Sparkles, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";

interface NavItemProps {
  name: string;
  href: string;
  icon: LucideIcon;
  isCollapsed?: boolean;
  badge?: string | number;
  isPremium?: boolean;
  subItems?: { name: string; href: string }[];
}

export const NavItem = ({ 
  name, 
  href, 
  icon: Icon, 
  isCollapsed, 
  badge, 
  isPremium,
  subItems 
}: NavItemProps) => {
  const location = useLocation();
  const { isPremium: userIsPremium } = useUserStore();
  const { toast } = useToast();
  const isActive = location.pathname === href || 
                  (subItems?.some(item => location.pathname === item.href));
  
  const handlePremiumClick = (e: React.MouseEvent) => {
    if (isPremium && !userIsPremium) {
      e.preventDefault();
      toast({
        title: "Premium Feature",
        description: `${name} is only available for Premium users.`,
        variant: "destructive",
      });
    }
  };
  
  // If there are subitems, we'll render a dropdown menu
  if (subItems && subItems.length > 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative group",
              isActive
                ? "bg-gradient-to-r from-emerald-500/10 to-sky-500/10 text-emerald-600 font-medium shadow-sm"
                : "text-gray-700 hover:bg-gray-100/70 hover:text-emerald-600"
            )}
          >
            <div className="flex items-center gap-3">
              <Icon className={cn(
                "w-5 h-5 flex-shrink-0 transition-transform duration-200", 
                isActive ? "text-emerald-600" : "text-gray-500",
                !isCollapsed && "group-hover:scale-110"
              )} />
              
              {!isCollapsed && <span className="truncate text-sm">{name}</span>}
            </div>
            
            {!isCollapsed && badge && (
              <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-600">
                {badge}
              </span>
            )}
            
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-xs text-white rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap shadow-lg">
                {name}
                {badge && <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded-full bg-emerald-500 text-white">{badge}</span>}
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          sideOffset={8} 
          className="w-56 bg-white border border-gray-200 shadow-lg rounded-lg p-1 animate-fade-in"
        >
          {subItems.map((item, i) => (
            <DropdownMenuItem key={i} asChild>
              <Link 
                to={item.href} 
                className={cn(
                  "flex items-center px-3 py-2 rounded-md text-sm transition-colors",
                  location.pathname === item.href ? "bg-emerald-50 text-emerald-600" : "text-gray-700 hover:bg-gray-50"
                )}
              >
                {item.name}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  
  // Standard nav item without dropdown
  return (
    <Link
      to={href}
      onClick={handlePremiumClick}
      className={cn(
        "flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 relative group",
        isActive
          ? "bg-gradient-to-r from-emerald-500/10 to-sky-500/10 text-emerald-600 font-medium shadow-sm"
          : "text-gray-700 hover:bg-gray-100/70 hover:text-emerald-600",
        isPremium && !userIsPremium && "opacity-75 hover:bg-amber-50/50"
      )}
    >
      <div className="flex items-center gap-3">
        <Icon className={cn(
          "w-5 h-5 flex-shrink-0 transition-transform duration-200", 
          isActive ? "text-emerald-600" : "text-gray-500",
          !isCollapsed && "group-hover:scale-110"
        )} />
        
        {!isCollapsed && (
          <div className="flex items-center gap-1">
            <span className="truncate text-sm">{name}</span>
            {isPremium && (
              isPremium && !userIsPremium ? (
                <Lock className="w-3.5 h-3.5 text-amber-500" />
              ) : (
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
              )
            )}
          </div>
        )}
      </div>
      
      {!isCollapsed && badge && (
        <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-600">
          {badge}
        </span>
      )}
      
      {isCollapsed && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-xs text-white rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 whitespace-nowrap shadow-lg">
          <div className="flex items-center gap-1">
            {name}
            {isPremium && (
              isPremium && !userIsPremium ? (
                <Lock className="w-3 h-3 text-amber-400" />
              ) : (
                <Sparkles className="w-3 h-3 text-amber-400" />
              )
            )}
          </div>
          {badge && <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded-full bg-emerald-500 text-white">{badge}</span>}
        </div>
      )}
      
      {isActive && !isCollapsed && (
        <div className="absolute right-2 w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
      )}
    </Link>
  );
};
